import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronLeft, FolderOpen } from 'lucide-react';
import { getApiBase } from '../lib/apiBase';
import { api } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { Button, LoadingScreen } from '../components/ui';
import { cn } from '../lib/utils';
import { pageStyles, detailStyles } from '../lib/styles';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { CustomerVehicleSummary } from '../components/cases/CustomerVehicleSummary';
import { LinkedBookingsSection } from '../components/cases/LinkedBookingsSection';
import { LinkedJobsSection } from '../components/cases/LinkedJobsSection';
import { CaseTimeline, type CaseTimelineEvent } from '../components/cases/CaseTimeline';
import { CaseAttachments, type CaseAttachmentsPayload } from '../components/cases/CaseAttachments';

// NOTE: /api/cases/:id currently returns an unwrapped payload (no {success,data}).
// We intentionally bypass the shared api client here.
export type CaseDetailResponse = {
  case: {
    id: string;
    customerId: string;
    vehicleId: string;
    status: 'OPEN' | 'CLOSED' | string;
    archivedAt: number | null;
    createdAt: number;
    updatedAt: number;
  };
  bookings: Array<{
    id: string;
    bookingReference: string | null;
    status: string;
    scheduledAt: number;
    checkedInAt: number | null;
    completedAt: number | null;
    createdAt: number;
    updatedAt: number;
  }>;
  jobs: Array<{
    id: string;
    bookingId: string;
    jobStatus: string;
    createdAt: number;
    updatedAt: number;
    completedAt: number | null;
  }>;
};

type CaseTimelinePayload = {
  case: CaseDetailResponse['case'];
  bookingIds: string[];
  jobIds: string[];
  events: CaseTimelineEvent[];
};

type CaseAttachmentsResponse = CaseAttachmentsPayload;


function formatDateTime(tsSeconds: number | null | undefined) {
  if (!tsSeconds) return '—';
  return new Date(tsSeconds * 1000).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const apiBase = getApiBase();
  const { success: showSuccess, error: showError } = useToast();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['cases', id],
    enabled: !!id,
    queryFn: async (): Promise<CaseDetailResponse> => {
      const res = await fetch(`${apiBase}/api/cases/${id}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        let msg = `Error al cargar el caso (${res.status})`;
        try {
          const json = (await res.json()) as { error?: string };
          if (json?.error) msg = json.error;
        } catch {
          // ignore
        }
        throw new Error(msg);
      }

      return (await res.json()) as CaseDetailResponse;
    },
    staleTime: 15_000,
  });

  const caseRow = data?.case;
  const {
    data: caseTimeline,
    isLoading: isTimelineLoading,
    refetch: refetchTimeline,
  } = useQuery({
    queryKey: ['cases', id, 'timeline'],
    enabled: !!id,
    queryFn: () => api.get<CaseTimelinePayload>(`/api/cases/${id}/timeline`),
    staleTime: 15_000,
  });

  const {
    data: caseAttachments,
    isLoading: isAttachmentsLoading,
    refetch: refetchAttachments,
  } = useQuery({
    queryKey: ['cases', id, 'attachments'],
    enabled: !!id,
    queryFn: () => api.get<CaseAttachmentsResponse>(`/api/cases/${id}/attachments`),
    staleTime: 15_000,
  });

  const refreshAll = () => {
    refetch();
    refetchTimeline();
    refetchAttachments();
  };

  const archiveCaseMutation = useMutation({
    mutationFn: () => {
      if (!id) {
        throw new Error('Falta el identificador del caso');
      }
      return api.post<CaseDetailResponse>(`/api/cases/${id}/archive`);
    },
    onSuccess: () => {
      showSuccess('Caso archivado');
      refreshAll();
    },
    onError: (error) => {
      showError(error instanceof Error ? error.message : 'Error al archivar el caso');
    },
  });


  const { data: customer } = useQuery({
    queryKey: ['customers', caseRow?.customerId],
    enabled: !!caseRow?.customerId,
    queryFn: () => api.get<{ id: string; name: string; phone?: string; email?: string }>(`/api/customers/${caseRow!.customerId}`),
    staleTime: 60_000,
  });

  const { data: vehicle } = useQuery({
    queryKey: ['vehicles', caseRow?.vehicleId],
    enabled: !!caseRow?.vehicleId,
    queryFn: () => api.get<{ id: string; registration?: string; make?: string; model?: string; year?: number; color?: string }>(`/api/vehicles/${caseRow!.vehicleId}`),
    staleTime: 60_000,
  });

  const sortedBookings = useMemo(() => {
    const items = data?.bookings || [];
    return [...items].sort((a, b) => (a.scheduledAt || 0) - (b.scheduledAt || 0));
  }, [data?.bookings]);

  const sortedJobs = useMemo(() => {
    const items = data?.jobs || [];
    return [...items].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  }, [data?.jobs]);

  const timelineEvents = caseTimeline?.events ?? [];
  const attachmentsPayload = caseAttachments?.attachments ?? { photos: [], signatures: [], files: [] };
  const linkedBookings = sortedBookings.map((b) => ({
    id: b.id,
    bookingReference: b.bookingReference,
    status: b.status,
    scheduledAt: b.scheduledAt,
    bookingType: undefined,
    caseId: caseRow?.id || null,
  }));
  const linkedJobs = sortedJobs.map((j) => ({
    id: j.id,
    bookingId: j.bookingId,
    jobStatus: j.jobStatus,
    createdAt: j.createdAt,
    completedAt: j.completedAt,
  }));

  if (isLoading) return <LoadingScreen />;

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <AlertCircle className="h-8 w-8 text-red-500 mb-3" />
        <p className="text-sm text-gray-900 mb-1">Error al cargar el caso</p>
        <p className="text-xs text-gray-500 mb-3">{(error as Error)?.message}</p>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            Reintentar
          </Button>
          <Link to="/jobs"><Button variant="secondary" size="sm">Volver</Button></Link>
        </div>
      </div>
    );
  }

  if (!data || !caseRow) {
    return (
      <div className="text-center py-8">
        <p className="text-xs text-gray-500">Caso no encontrado</p>
        <Link to="/jobs" className="text-xs text-blue-600 hover:underline mt-2 block">Volver</Link>
      </div>
    );
  }

  const vehicleLabel = vehicle
    ? [vehicle.registration, [vehicle.make, vehicle.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ')
    : caseRow.vehicleId;

  return (
    <div className={detailStyles.container}>
      <div className={pageStyles.header}>
        <div className={pageStyles.headerLeft}>
          <Link to="/jobs" className={pageStyles.backButton}>
            <ChevronLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className={pageStyles.title}>Caso</h1>
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border',
                  caseRow.status === 'OPEN'
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-gray-50 text-gray-700 border-gray-200'
                )}
              >
                <FolderOpen className="h-3 w-3" />
                {caseRow.status}
              </span>
            </div>
            <p className={pageStyles.subtitle}>
              {customer?.name ? `${customer.name} · ` : ''}
              {vehicleLabel}
            </p>
          </div>
        </div>
      </div>

      <CustomerVehicleSummary
        customer={customer ? { id: customer.id, name: customer.name, phone: customer.phone, email: customer.email } : undefined}
        vehicle={vehicle ? { id: vehicle.id, registration: vehicle.registration, make: vehicle.make, model: vehicle.model, year: vehicle.year, color: vehicle.color } : undefined}
      />

      <div className={detailStyles.grid}>
        <div className={detailStyles.main}>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">Resumen</div>
                <div className="text-[11px] text-muted-foreground font-mono">{caseRow.id}</div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Creado</span>
                <span>{formatDateTime(caseRow.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Actualizado</span>
                <span>{formatDateTime(caseRow.updatedAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Cliente</span>
                <Link to={`/customers/${caseRow.customerId}`} className="text-blue-600 hover:underline truncate">
                  {customer?.name || caseRow.customerId}
                </Link>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Vehículo</span>
                <Link to={`/vehicles/${caseRow.vehicleId}`} className="text-blue-600 hover:underline truncate">
                  {vehicleLabel}
                </Link>
              </div>
            </CardContent>
          </Card>

          <LinkedBookingsSection bookings={linkedBookings} />
          <LinkedJobsSection jobs={linkedJobs} />
          <CaseTimeline events={timelineEvents} loading={isTimelineLoading} />
          <CaseAttachments
            attachments={attachmentsPayload}
            loading={isAttachmentsLoading}
            apiBase={apiBase}
          />
        </div>

        <div className={detailStyles.sidebar}>
          <Card>
            <CardHeader className="pb-2">
              <div className="text-sm font-semibold">Acciones</div>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to="/jobs" className="block">
                <Button variant="outline" className="w-full">Volver al Job Board</Button>
              </Link>
              <Button variant="outline" className="w-full" onClick={refreshAll}>
                Actualizar
              </Button>
              <Button
                variant="destructive"
                className="w-full"
                disabled={caseRow?.status === 'CLOSED' || archiveCaseMutation.isPending}
                onClick={() => archiveCaseMutation.mutate()}
              >
                {caseRow?.status === 'CLOSED' ? 'Caso archivado' : 'Archivar caso'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
